import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { DomainManagerService } from '../service/domain-manager.service';
import { SidebarStateService } from '../service/sidebar-state.service';
import { SessionTimerService } from '../service/session-timer.service';

const MOBILE_BP = 991;

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  public activeTab: any;
  username: any;

  isDropdownOpen = false;

  // for mobile collapse toggle
  mobileCollapsed = true;
  routerSub!: Subscription;
  isRadiologyDomain: boolean = false;
  logoUrl: string = '';

  // submenu open/close states
  isOpen: { [key: string]: boolean } = {
    clients: false,
    logs: false
  };

  // Route prefixes that belong to each submenu — kept in sync with the
  // [class.nav-dropdown--active] checks in header.component.html.
  private readonly clientsRoutePrefixes = [
    '/user', '/client-directory', '/client-offer', '/coupon-code', '/whatsapp-messages'
  ];
  private readonly logsRoutePrefixes = ['/log-in-log', '/error-log'];

  readonly appVersion: string = environment.appVersion;
  readonly today: number = Date.now();

  constructor(
    public router: Router,
    private readonly domainService: DomainManagerService,
    public readonly sidebarState: SidebarStateService,
    private readonly sessionTimer: SessionTimerService
  ) { }

  /** Viewport-aware toggle: mobile → drawer, desktop → sidebar collapse to icon rail. */
  toggleSidebar(): void {
    if (window.matchMedia(`(max-width: ${MOBILE_BP}px)`).matches) {
      this.mobileCollapsed = !this.mobileCollapsed;
    } else {
      this.sidebarState.toggle();
    }
  }

  get userInitials(): string {
    const first = this.username?.FirstName?.charAt(0) ?? '';
    const last = this.username?.LastName?.charAt(0) ?? '';
    return (first + last).toUpperCase() || '?';
  }

  get productLabel(): string {
    if (this.domainService.isRadiologyDomain) return 'Plus Radiology';
    if (this.domainService.isDoctorDomain) return 'Plus Doctor';
    if (this.domainService.isPediatricDomain) return 'Plus Pediatric';
    if (this.domainService.isGynecologistDomain) return 'Plus Gynecologist';
    return 'Plus Physio';
  }

  ngOnInit() {
    this.isRadiologyDomain = this.domainService.isRadiologyDomain;
    this.logoUrl = this.domainService.getLogoUrl();
    this.username = JSON.parse(localStorage['loginData'] ?? '{}');

    // Keep the submenu that owns the current route expanded (e.g. clicking
    // "Client Directory" should not collapse the "Clients" group it lives in).
    this.syncOpenWithRoute(this.router.url);

    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.mobileCollapsed = true;
      } else if (event instanceof NavigationEnd) {
        this.syncOpenWithRoute(event.urlAfterRedirects);
      }
    });
  }

  private syncOpenWithRoute(url: string): void {
    this.isOpen['clients'] = this.clientsRoutePrefixes.some(prefix => url.startsWith(prefix));
    this.isOpen['logs'] = this.logsRoutePrefixes.some(prefix => url.startsWith(prefix));
  }

  navigate() {
    this.router.navigate(['/user']);
  }

  navigator() {
    this.router.navigate(['/dashboard']);
  }
  onLogout() {
    this.sessionTimer.stop();
    localStorage.setItem('isLogin', JSON.stringify(false))
    this.router.navigate(['login']);
    localStorage.clear();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;

    const clickedInsideUserDropdown = !!target.closest('.dropdown-container');

    const clickedInsideNavDropdown = !!target.closest('.nav-dropdown');

    if (!clickedInsideUserDropdown) {
      this.isDropdownOpen = false;
    }

    if (!clickedInsideNavDropdown) {
      // Reopen whichever submenu owns the current route (if any) instead of
      // force-closing everything — e.g. a click on the page content while on
      // /client-directory should leave "Clients" expanded, not collapse it.
      this.syncOpenWithRoute(this.router.url);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (window.innerWidth > MOBILE_BP && !this.mobileCollapsed) {
      this.mobileCollapsed = true;
    }
  }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent) {
    const target = event.target as HTMLElement;
    const focusedInsideNavDropdown = !!target.closest('.nav-dropdown');

    if (!focusedInsideNavDropdown) {
      this.syncOpenWithRoute(this.router.url);
    }
  }

  toggleDropdown(event: Event, key: string) {
    event.stopPropagation();
    this.isOpen[key] = !this.isOpen[key];
  }

  toggleSubmenu(event: Event, key: string) {
    event.preventDefault();
    for (const k of Object.keys(this.isOpen)) {
      if (k === key) {
        this.isOpen[k] = !this.isOpen[k];
      } else {
        this.isOpen[k] = false;
      }
    }
  }

  ngOnDestroy() {
    this.routerSub?.unsubscribe();
  }
}
